/**
 * WizardStepIndicator - Progress indicator for the Add Plane wizard
 *
 * Displays 4 steps with visual feedback:
 * - Completed steps: checkmark icon, green color
 * - Current step: blue/primary highlight
 * - Future steps: gray/muted
 */

import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'

interface WizardStepIndicatorProps {
  steps: readonly string[]
  currentStep: number
  isStepComplete: (step: number) => boolean
  onStepClick?: (step: number) => void
  canNavigate?: boolean
}

export function WizardStepIndicator({
  steps,
  currentStep,
  isStepComplete,
  onStepClick,
  canNavigate = false,
}: WizardStepIndicatorProps) {
  return (
    <div className="flex items-center justify-center w-full mb-6">
      {steps.map((stepLabel, index) => {
        const step = index
        const isCompleted = isStepComplete(step)
        const isCurrent = step === currentStep
        const isPast = step < currentStep
        const canClick = canNavigate && (isCompleted || isPast)

        return (
          <div key={stepLabel} className="flex items-center">
            {/* Step circle */}
            <button
              type="button"
              disabled={!canClick}
              onClick={() => canClick && onStepClick?.(step)}
              className={cn(
                'relative flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold transition-all duration-300',
                // Completed state
                isCompleted && !isCurrent && 'bg-green-500 text-white',
                // Current state
                isCurrent &&
                  'bg-primary text-primary-foreground shadow-lg ring-4 ring-primary/20',
                // Future state
                !isCompleted &&
                  !isCurrent &&
                  'bg-muted text-muted-foreground',
                // Clickable state
                canClick && 'cursor-pointer hover:scale-110',
                !canClick && 'cursor-default'
              )}
            >
              {isCompleted && !isCurrent ? (
                <Check className="w-4 h-4" />
              ) : (
                index + 1
              )}
            </button>

            {/* Step label (below circle on larger screens) */}
            <span
              className={cn(
                'hidden sm:block absolute -bottom-6 left-1/2 -translate-x-1/2 text-xs whitespace-nowrap transition-colors',
                isCurrent && 'text-primary font-medium',
                isCompleted && !isCurrent && 'text-green-600',
                !isCompleted && !isCurrent && 'text-muted-foreground'
              )}
              style={{ position: 'relative', bottom: 'auto', left: 'auto', transform: 'none' }}
            >
              {/* Label rendered separately for clean positioning */}
            </span>

            {/* Connector line (except after last step) */}
            {index < steps.length - 1 && (
              <div
                className={cn(
                  'w-12 h-1 mx-2 transition-colors duration-300',
                  isPast || (isCurrent && isCompleted)
                    ? 'bg-green-500'
                    : 'bg-muted'
                )}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}

// Compact version with labels
export function WizardStepIndicatorWithLabels({
  steps,
  currentStep,
  isStepComplete,
  onStepClick,
  canNavigate = false,
}: WizardStepIndicatorProps) {
  return (
    <div className="w-full mb-8">
      <div className="flex items-center justify-between">
        {steps.map((stepLabel, index) => {
          const step = index
          const isCompleted = isStepComplete(step)
          const isCurrent = step === currentStep
          const isPast = step < currentStep
          const canClick = canNavigate && (isCompleted || isPast)

          return (
            <div
              key={stepLabel}
              className={cn(
                'flex flex-col items-center flex-1',
                index < steps.length - 1 && 'relative'
              )}
            >
              {/* Connector line */}
              {index < steps.length - 1 && (
                <div
                  className={cn(
                    'absolute top-4 left-[50%] w-full h-0.5 -translate-y-1/2',
                    isPast || isCompleted ? 'bg-green-500' : 'bg-muted'
                  )}
                />
              )}

              {/* Step circle */}
              <button
                type="button"
                disabled={!canClick}
                onClick={() => canClick && onStepClick?.(step)}
                className={cn(
                  'relative z-10 flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold transition-all duration-300',
                  isCompleted && !isCurrent && 'bg-green-500 text-white',
                  isCurrent &&
                    'bg-primary text-primary-foreground shadow-lg ring-4 ring-primary/20',
                  !isCompleted &&
                    !isCurrent &&
                    'bg-muted text-muted-foreground',
                  canClick && 'cursor-pointer hover:scale-110',
                  !canClick && 'cursor-default'
                )}
              >
                {isCompleted && !isCurrent ? (
                  <Check className="w-4 h-4" />
                ) : (
                  index + 1
                )}
              </button>

              {/* Step label */}
              <span
                className={cn(
                  'mt-2 text-xs font-medium transition-colors',
                  isCurrent && 'text-primary',
                  isCompleted && !isCurrent && 'text-green-600',
                  !isCompleted && !isCurrent && 'text-muted-foreground'
                )}
              >
                {stepLabel}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default WizardStepIndicator
